import type { Athlete, SportTypeField } from './types'

/** Полных лет на дату. Возраст не хранится, а считается из даты рождения (ТЗ стр. 4). */
export function ageOn(dateOfBirth: string | undefined, now: Date): number | null {
  if (!dateOfBirth) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth)
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  let age = now.getFullYear() - y
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)) age--
  return age >= 0 && age < 120 ? age : null
}

/** «7 лет», «1 год», «3 года» */
export function pluralYears(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} год`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} года`
  return `${n} лет`
}

/** «Стрижи / 7 лет»: то, чем различают однофамильцев в списке (S07). */
export function athleteMeta(a: Athlete, groupName: string | undefined, now: Date): string {
  const age = ageOn(a.date_of_birth, now)
  const parts = [groupName ?? 'Группа не назначена']
  if (age !== null) parts.push(pluralYears(age))
  return parts.join(' / ')
}

/** Ё и регистр не различаем, лишние пробелы убираем. */
export function normalizeName(name: string): string {
  return name.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()
}

export function matchesQuery(a: Athlete, query: string): boolean {
  const q = normalizeName(query)
  if (!q) return true
  return normalizeName(a.name).includes(q)
}

/**
 * Возможные дубли нового спортсмена (SC04): то же имя целиком или та же
 * фамилия с именем, начинающимся так же («Иванов Артём» и «Иванов Артемий»).
 * Ничего не объединяем, только предупреждаем.
 *
 * Только для синтетики (fakeApi). Экраны спрашивают сервер
 * (`findSimilarAthletes`): он знает уменьшительные и порядок слов.
 */
export function possibleDuplicates(name: string, existing: Athlete[]): Athlete[] {
  const [surname, first = ''] = normalizeName(name).split(' ')
  if (!surname) return []
  return existing.filter((a) => {
    const [s, f = ''] = normalizeName(a.name).split(' ')
    if (s !== surname) return false
    if (!first || !f) return true
    const n = Math.min(first.length, f.length, 4)
    return first.slice(0, n) === f.slice(0, n)
  })
}

/** Значение спортивного поля строкой; пустое - null. */
export function sportValue(a: Athlete, key: string): string | null {
  const v = a.sport_data?.[key]
  if (v === undefined || v === null || v === '') return null
  return String(v)
}

/** Заполненные поля шаблона - для бейджей и блока «Квалификация». */
export function filledSportFields(a: Athlete, fields: SportTypeField[]): { field: SportTypeField; value: string }[] {
  return fields
    .map((field) => ({ field, value: sportValue(a, field.field_key) }))
    .filter((x): x is { field: SportTypeField; value: string } => x.value !== null)
}

/** Ключи sport_data, которых нет в шаблоне: старые значения, их нельзя терять. */
export function legacySportKeys(a: Athlete, fields: SportTypeField[]): { key: string; value: string }[] {
  const known = new Set(fields.map((f) => f.field_key))
  return Object.entries(a.sport_data ?? {})
    .filter(([k, v]) => !known.has(k) && v !== '' && v !== null && v !== undefined)
    .map(([key, value]) => ({ key, value: String(value) }))
}

/** На паузе: уехал, вернётся позже. В группе остаётся, оплату с него не ждём. */
export function isPaused(a: { paused_at?: string }): boolean {
  return !!a.paused_at
}

/** Тот же порядок, но спортсмены на паузе в конце. */
export function pausedLast<T extends { paused_at?: string }>(list: T[]): T[] {
  return [...list.filter((a) => !isPaused(a)), ...list.filter(isPaused)]
}
